class LuvatyTheme {
  constructor() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.header = document.querySelector('[data-site-header]');
    this.progress = document.querySelector('.scroll-progress span');
    this.init();
  }

  init() {
    this.bindCategoryRoute();
    this.bindActiveNavigation();
    this.bindScroll();
    this.bindReveals();
    this.bindDetails();
    this.bindHeroVideo();
    this.bindProductVariants();
    this.bindProductDetails();
    document.documentElement.classList.add('theme-ready');
  }

  bindActiveNavigation() {
    if (!this.header) return;
    const links = [...this.header.querySelectorAll('[data-primary-link]')];
    const update = () => {
      let active = this.header.dataset.activeStage;
      let currentType = this.header.dataset.currentType || 'page';
      const params = new URLSearchParams(window.location.search);
      if (document.body.classList.contains('category-route')) {
        const stage = params.get('stage');
        if (['perimenopause', 'menopause'].includes(stage)) {
          active = stage;
          currentType = 'true';
        }
      } else if (!active) {
        const sectionLink = links.find((link) => {
          const target = new URL(link.href);
          return target.hash && target.pathname === window.location.pathname && target.hash === window.location.hash;
        });
        active = sectionLink?.dataset.primaryLink;
        currentType = 'location';
      }
      links.forEach((link) => {
        if (link.dataset.primaryLink === active) link.setAttribute('aria-current', currentType);
        else link.removeAttribute('aria-current');
      });
    };
    window.addEventListener('hashchange', update);
    window.addEventListener('pageshow', update);
    update();
  }

  bindScroll() {
    let ticking = false;
    const update = () => {
      const top = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      document.body.classList.remove('home-header-hidden');
      this.header?.classList.toggle('is-scrolled', top > 24);
      if (this.progress && max > 0) this.progress.style.transform = `scaleX(${Math.min(top / max, 1)})`;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    update();
  }

  bindReveals() {
    document.querySelectorAll('[data-reveal]').forEach((item) => item.classList.add('is-visible'));
  }

  bindDetails() {
    document.querySelectorAll('details[data-drawer]').forEach((drawer) => {
      const toggle = drawer.querySelector('summary');
      const close = drawer.querySelector('[data-drawer-close]');
      const closeDrawer = () => {
        drawer.removeAttribute('open');
        if (drawer.contains(document.activeElement)) toggle?.focus({ preventScroll: true });
      };

      drawer.addEventListener('toggle', () => {
        document.documentElement.classList.toggle('drawer-open', drawer.open);
        toggle?.setAttribute('aria-label', drawer.open ? drawer.dataset.closeLabel : drawer.dataset.openLabel);
        toggle?.setAttribute('aria-expanded', String(drawer.open));
        if (drawer.open) close?.focus({ preventScroll: true });
      });
      close?.addEventListener('click', closeDrawer);
      drawer.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeDrawer));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && drawer.open) closeDrawer();
        if (event.key === 'Tab' && drawer.open) {
          const controls = [...drawer.querySelectorAll('button, a[href]')];
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }
      });
    });
  }

  bindCategoryRoute() {
    const pages = document.querySelector('[data-category-pages]');
    if (!pages) return;

    pages.querySelectorAll('h1').forEach((heading) => {
      const secondaryHeading = document.createElement('h2');
      secondaryHeading.dataset.routePrimaryHeading = '';
      secondaryHeading.className = heading.className;
      secondaryHeading.innerHTML = heading.innerHTML;
      heading.replaceWith(secondaryHeading);
    });

    const params = new URLSearchParams(window.location.search);
    const requestedCategory = params.get('category');
    const requestedStage = params.get('stage');
    const category = requestedCategory === 'well-aging' ? 'menopause-wellness' : requestedCategory;
    if (!['skin-care', 'menopause-wellness'].includes(category)) return;

    const stagesByCategory = {
      'skin-care': ['20s', '30s'],
      'menopause-wellness': ['perimenopause', 'menopause', 'beyond']
    };
    const stage = stagesByCategory[category].includes(requestedStage) ? requestedStage : null;
    const activePage = stage
      ? pages.querySelector(`[data-stage-page="${stage}"]`)
      : pages.querySelector(`[data-category-page="${category}"]`);
    if (!activePage) return;

    document.body.classList.add('category-route');
    document.querySelectorAll('#MainContent > .shopify-section').forEach((section) => {
      if (!section.contains(pages)) section.hidden = true;
    });
    pages.hidden = false;
    activePage.hidden = false;
    const homeHeading = document.querySelector('.luvaty-hero h1');
    if (homeHeading) {
      const secondaryHeading = document.createElement('h2');
      secondaryHeading.className = homeHeading.className;
      secondaryHeading.innerHTML = homeHeading.innerHTML;
      homeHeading.replaceWith(secondaryHeading);
    }
    const routeHeading = activePage.querySelector('[data-route-primary-heading]');
    if (routeHeading && routeHeading.tagName !== 'H1') {
      const primaryHeading = document.createElement('h1');
      primaryHeading.dataset.routePrimaryHeading = '';
      primaryHeading.innerHTML = routeHeading.innerHTML;
      routeHeading.replaceWith(primaryHeading);
    }
    document.body.classList.toggle('stage-route', Boolean(stage));
    document.title = `${activePage.dataset.stageTitle || activePage.dataset.categoryTitle} | Luvaty`;
    window.scrollTo(0, 0);
  }

  bindHeroVideo() {
    const video = document.querySelector('[data-hero-video]');
    if (!video || this.reducedMotion || document.body.classList.contains('category-route')) return;

    const breakpoint = window.matchMedia('(max-width: 900px)');
    const isRouteReturn = document.documentElement.classList.contains('route-transition-pending');
    let isVisible = true;
    let revealFrame = 0;
    let resumePending = false;

    if (isRouteReturn) document.documentElement.classList.add('hero-video-resuming');

    const storageKey = () => breakpoint.matches ? 'luvaty-hero-time-mobile' : 'luvaty-hero-time-desktop';

    const savedTime = () => {
      try {
        const value = Number(window.sessionStorage.getItem(storageKey()));
        return Number.isFinite(value) && value > .1 ? value : 0;
      } catch (_) {
        return 0;
      }
    };

    const saveTime = () => {
      if (!Number.isFinite(video.currentTime) || video.currentTime <= .1) return;
      try { window.sessionStorage.setItem(storageKey(), String(video.currentTime)); } catch (_) {}
    };

    const playWhenReady = () => {
      if (document.hidden || !isVisible) return;
      video.play().catch(() => {});
    };

    const setSource = () => {
      const nextSource = breakpoint.matches ? video.dataset.mobileSrc : video.dataset.desktopSrc;
      if (!nextSource || video.dataset.activeSource === nextSource) return;
      video.pause();
      video.classList.remove('is-ready');
      video.src = nextSource;
      video.dataset.activeSource = nextSource;
      video.load();
    };

    const restoreTime = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      const stored = savedTime();
      const requested = isRouteReturn ? (stored || .18) : 0;
      if (requested <= .1) return;
      const safeTime = requested >= video.duration - .5 ? .5 : requested;
      resumePending = true;
      video.currentTime = safeTime;
    };

    const revealOncePlaying = () => {
      window.cancelAnimationFrame(revealFrame);
      const initialTime = video.currentTime;
      const startedAt = performance.now();

      const checkProgress = () => {
        if (video.paused || document.hidden || !isVisible) return;
        const hasAdvanced = video.currentTime >= initialTime + 0.035;
        const safetyTimeoutReached = performance.now() - startedAt > 300;
        if (hasAdvanced || safetyTimeoutReached) {
          video.classList.add('is-ready');
          window.setTimeout(() => document.documentElement.classList.remove('hero-video-resuming'), 300);
          return;
        }
        revealFrame = window.requestAnimationFrame(checkProgress);
      };

      revealFrame = window.requestAnimationFrame(checkProgress);
    };

    video.muted = true;
    video.addEventListener('loadedmetadata', restoreTime);
    video.addEventListener('seeked', () => {
      resumePending = false;
      playWhenReady();
    });
    video.addEventListener('canplay', () => {
      if (!resumePending) playWhenReady();
    });
    video.addEventListener('playing', revealOncePlaying);
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        isVisible = entry.isIntersecting;
        if (isVisible) playWhenReady();
        else video.pause();
      }, { threshold: 0.15 });
      observer.observe(video);
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        saveTime();
        video.pause();
      }
      else playWhenReady();
    });
    window.addEventListener('pagehide', saveTime);
    window.addEventListener('pageshow', playWhenReady);
    breakpoint.addEventListener('change', setSource);
    setSource();
  }

  bindProductDetails() {
    document.querySelectorAll('.product-main a[href^="#Component-"]').forEach((link) => {
      link.addEventListener('click', () => {
        const details = document.getElementById(link.hash.slice(1));
        if (details?.tagName === 'DETAILS') details.open = true;
      });
    });
  }

  bindProductVariants() {
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      const select = form.querySelector('select[name="id"]');
      const price = form.closest('.product-info')?.querySelector('[data-product-price]');
      const button = form.querySelector('button[type="submit"]');
      if (!select) return;
      select.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        if (price && option.dataset.price) price.textContent = option.dataset.price;
        if (button) {
          button.disabled = option.disabled;
          button.textContent = option.disabled ? 'Sold out' : 'Add to bag';
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', select.value);
        window.history.replaceState({}, '', url);
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new LuvatyTheme());
